<script setup lang="ts">
import { BaseButton, BasePassword } from '@components/base';
import { AUTH_ROUTES, buildLoginUrl } from '@constants/auth-routes';
import { passwordRules } from '@helpers/validators';
import { SetPasswordPayload } from '@models/user';
import { useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required, sameAs } from '@vuelidate/validators';
import { computed, onMounted, reactive, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    content?: Record<string, string>;
    inDrawer?: boolean;
    initialSuccess?: boolean;
  }>(),
  { content: () => ({}), inDrawer: false, initialSuccess: false },
);

const emit = defineEmits<{ close: []; submitted: [] }>();

const userStore = useUserStore();
const searchParams = new URLSearchParams(window.location.search);

const formData = reactive({
  newPassword: '',
  confirmPassword: '',
});

const code = computed(() => searchParams.get('code')?.trim() ?? '');
const id = computed(() => searchParams.get('id')?.trim() ?? '');
const isTokenMissing = computed(() => !code.value || !id.value);

const confirmMatches = helpers.withMessage(
  'Confirm Password must match New Password.',
  sameAs(computed(() => formData.newPassword)),
);

const rules = {
  newPassword: passwordRules,
  confirmPassword: {
    required,
    confirmMatches,
  },
};

const v$ = useVuelidate(rules, formData);

const title = computed(() => props.content?.resetTitle ?? props.content?.title ?? 'Reset your password');
const instruction = computed(() => props.content?.resetInstruction ?? props.content?.instruction ?? 'Enter a new password below to change your password.');
const invalidLinkText = computed(() => props.content?.invalidLink ?? 'This password reset link is invalid or has expired.');
const backToForgotText = computed(() => props.content?.backToForgot ?? 'Request a new reset link');
const backToLoginText = computed(() => props.content?.backToLogin ?? 'Back to login');
const successTitle = computed(() => props.content?.passwordChangedTitle ?? 'Password changed');
const successInstruction = computed(() => props.content?.passwordChangedInstruction ?? 'Your password has changed. You can now login with your new password.');
const loginCtaText = computed(() => props.content?.backToLogin ?? props.content?.loginCta ?? 'Back to login');
const newPasswordError = computed(() => v$.value.newPassword.$errors[0]?.$message ?? '');
const confirmPasswordError = computed(() => v$.value.confirmPassword.$errors[0]?.$message ?? '');
const isSuccess = ref(props.initialSuccess);
const isTokenInvalid = ref(false);
const isTokenProblem = computed(() => isTokenMissing.value || isTokenInvalid.value);

const isSetPasswordSuccess = (result: { errors?: string[] } | undefined): boolean => {
  if (!result) {
    return false;
  }

  return !result.errors?.length;
};

const onSubmit = async () => {
  if (isTokenProblem.value) {
    return;
  }

  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }

  const payload: SetPasswordPayload = {
    code: code.value,
    id: id.value,
    newPassword: formData.newPassword,
  };

  const result = await userStore.setPassword(payload, { quiet: props.inDrawer });
  if (!isSetPasswordSuccess(result)) {
    const joinedErrors = (result?.errors ?? []).join(' ').toLowerCase();
    if (
      joinedErrors.includes('expired')
      || joinedErrors.includes('invalid')
      || joinedErrors.includes('reset link')
      || joinedErrors.includes('token')
    ) {
      isTokenInvalid.value = true;
    }
    return;
  }

  if (props.inDrawer) {
    isSuccess.value = true;
    emit('submitted');
    return;
  }

  window.location.assign(AUTH_ROUTES.PASSWORD_CHANGED);
};

const onBackToLogin = () => {
  if (props.inDrawer) {
    emit('close');
    return;
  }

  window.location.href = buildLoginUrl();
};

onMounted(() => {
  if (props.initialSuccess) {
    isSuccess.value = true;
  }
});
</script>

<template>
  <form
    v-if="!isSuccess"
    class="flex flex-col gap-24"
    @submit.prevent="onSubmit"
  >
    <h1
      v-if="!inDrawer"
      class="font-inter-tight text-20 font-semibold text-off-black-500 lg:text-26"
    >
      {{ title }}
    </h1>

    <p class="text-14 font-[450] text-off-black-500">
      {{ isTokenProblem ? invalidLinkText : instruction }}
    </p>

    <div
      v-if="!isTokenProblem"
      class="flex flex-col gap-16"
    >
      <BasePassword
        v-model="formData.newPassword"
        placeholder="New password"
        autocomplete="new-password"
        :error="v$.newPassword.$error"
        :error-message="(newPasswordError as unknown) as string"
      />
      <BasePassword
        v-model="formData.confirmPassword"
        placeholder="Re-enter new password"
        autocomplete="new-password"
        :error="v$.confirmPassword.$error"
        :error-message="(confirmPasswordError as unknown) as string"
      />
    </div>

    <BaseButton
      v-if="!isTokenProblem"
      text="RESET PASSWORD"
      type="submit"
      variant="primary"
      class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
      :loading="userStore.loading"
      :disabled="v$.$error"
    >
      <template #icon-right>
        <span
          class="size-20 opacity-0"
          aria-hidden="true"
        />
      </template>
    </BaseButton>

    <p class="text-center text-14 text-off-black-500">
      <template v-if="!isTokenProblem">
        Remember your password?
      </template>
      <a
        v-if="isTokenProblem && !inDrawer"
        :href="AUTH_ROUTES.FORGOT_PASSWORD"
        class="font-medium text-teal-500 hover:underline"
      >
        {{ backToForgotText }}
      </a>
      <button
        v-else-if="isTokenProblem && inDrawer"
        type="button"
        class="font-medium text-teal-500 hover:underline"
        @click="emit('close')"
      >
        {{ backToForgotText }}
      </button>
      <a
        v-else-if="!inDrawer"
        :href="AUTH_ROUTES.LOGIN"
        class="font-medium text-teal-500 hover:underline"
      >
        {{ backToLoginText }}
      </a>
      <button
        v-else
        type="button"
        class="font-medium text-teal-500 hover:underline"
        @click="onBackToLogin"
      >
        {{ backToLoginText }}
      </button>
    </p>
  </form>

  <div
    v-else
    class="flex flex-col gap-28"
  >
    <div>
      <h2 class="text-18 font-[400] text-off-black-500">
        {{ successTitle }}
      </h2>

      <p class="mt-16 text-14 font-[400] text-[#6C6A66]">
        {{ successInstruction }}
      </p>
    </div>

    <BaseButton
      :text="loginCtaText"
      type="button"
      variant="primary"
      class="!flex !h-[44px] !min-h-[44px] !w-full items-center justify-center !rounded-full !py-0 !text-16 !font-medium !uppercase"
      @click="onBackToLogin"
    />
  </div>
</template>
